import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import type { TFunction } from 'i18next';
import { useTenantApp } from '@/hooks';
import { formatTenantAppTime } from '@/utils/date';
import { Icon } from '../../Icon';

const LiveOccupancyWidget = ({ current, total, temperature, updatedAt, t }: { current?: number, total?: number, temperature?: number, updatedAt?: number, t: TFunction }) => {
    const { app } = useTenantApp();
    const hasCurrent = current !== undefined;
    const hasTotal = total !== undefined;
    const hasOccupancy = hasCurrent || hasTotal;
    const hasTemp = temperature !== undefined;

    if (!hasOccupancy && !hasTemp) return null;

    // Date formatting logic
    // TODO: make locale dynamic based on app language, currently hardcoded to Italian
    const getUpdateLabel = (timestamp: number) => {
        try {
            const date = new Date(timestamp);

            // "2 min fa"
            const distance = formatDistanceToNow(date, { addSuffix: true, locale: it });
            const exactTime = formatTenantAppTime(timestamp, app?.timezone);

            return { distance, exactTime };
        } catch {
            return { distance: '', exactTime: '' };
        }
    };

    const updateInfo = updatedAt ? getUpdateLabel(updatedAt) : null;

    // percentage calculation with safety checks
    const canShowProgress = hasCurrent && hasTotal && total > 0;
    const percentage = canShowProgress ? Math.round((current! / total!) * 100) : 0;

    return (
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5">
            <div className={`flex items-start mb-4 ${hasOccupancy && hasTemp ? 'justify-between' : 'justify-start gap-8'}`}>

                {/* OCCUPANCY / CROWD BLOCK */}
                {hasOccupancy && (
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-primary">
                                {canShowProgress ? t('poiModal.liveOccupancy.occupancy') : (hasCurrent ? t('poiModal.liveOccupancy.current') : t('poiModal.liveOccupancy.capacity'))}
                            </h4>
                            {hasCurrent && (
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary"></span>
                                </span>
                            )}
                        </div>

                        <div className="flex items-baseline gap-1">
                            <p className="text-3xl font-black tracking-tight">
                                {hasCurrent ? current : total}
                                {canShowProgress && (
                                    <span className="text-sm opacity-30 font-medium ml-1">/ {total}</span>
                                )}
                            </p>
                            {!canShowProgress && hasTotal && !hasCurrent && (
                                <span className="text-xs font-bold opacity-40 uppercase ml-1">{t('poiModal.liveOccupancy.capacity')}</span>
                            )}
                            {!canShowProgress && hasCurrent && !hasTotal && (
                                <span className="text-xs font-bold opacity-40 uppercase ml-1">{t('poiModal.liveOccupancy.current')}</span>
                            )}
                        </div>
                    </div>
                )}

                {/* TEMPERATURE BLOCK */}
                {hasTemp && (
                    <div className={hasOccupancy ? "text-right" : "flex-1"}>
                        <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">
                            {t('poiModal.liveOccupancy.waterTemperature')}
                        </h4>
                        <div className="flex items-baseline gap-1 justify-end">
                            <p className="text-3xl font-black">{temperature}</p>
                            <span className="text-lg font-bold opacity-40">°C</span>
                        </div>
                    </div>
                )}
            </div>

            {/* PROGRESS BAR OR STATUS BADGE */}
            {canShowProgress ? (
                <div className="space-y-2">
                    <progress
                        className={`progress w-full h-3 rounded-full ${percentage > 85 ? 'progress-error' : 'progress-primary'}`}
                        value={percentage}
                        max="100"
                    ></progress>
                    <div className="flex justify-between text-[10px] font-bold opacity-60 uppercase">
                        <span>{percentage}% {t('poiModal.liveOccupancy.occupied')}</span>
                        <span>{percentage > 85 ? t('poiModal.liveOccupancy.limitedAvailability') : t('poiModal.liveOccupancy.available')}</span>
                    </div>
                </div>
            ) : (
                hasOccupancy && (
                    <div className="mt-2 pt-2 border-t border-primary/10">
                        <span className="text-[10px] font-bold opacity-40 uppercase tracking-widest">
                            {hasCurrent ? t('poiModal.liveOccupancy.dataFromSensors') : t('poiModal.liveOccupancy.theoreticalMaxCapacity')}
                        </span>
                    </div>
                )
            )}

            {/* FOOTER SMART WITH DATE-FNS */}
            <div className="mt-2 pt-3 border-t border-primary/10 flex justify-between items-center text-[9px] font-black uppercase tracking-widest opacity-40">
                <div className="flex items-center gap-1.5">
                    <Icon name="sensors" className="text-[14px]" />
                    <span>{hasCurrent ? t('poiModal.liveOccupancy.realTimeData') : t('poiModal.liveOccupancy.estimatedData')}</span>
                </div>

                {updateInfo && (
                    <div className="flex items-center gap-1 tooltip tooltip-left" data-tip={`Ore ${updateInfo.exactTime}`}>
                        <Icon name="history" className="text-[14px]" />
                        <span>{updateInfo.distance}</span>
                    </div>
                )}
            </div>
        </div>


    );
};

export default LiveOccupancyWidget;



